import { cn } from "@/utils/theme";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Checkbox,
  inputVariants,
  Icon,
} from "@elaniintc/goes-design-system";
import { useMemo, useRef, useState, type ReactNode } from "react";

interface Props {
  selectedItems: string[];
  onChange: (selected: string[]) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isLoading?: boolean;
  triggerIcon?: ReactNode;
  srOnlyLabel?: boolean;
  label?: string;
  required?: boolean;
  name?: string;
  emptyStateMessage?: string;
}

const MultiSelect = ({
  className,
  isLoading,
  onChange,
  options,
  selectedItems,
  disabled,
  placeholder,
  triggerIcon,
  srOnlyLabel,
  label,
  required,
  name,
  emptyStateMessage,
  ...props
}: Props) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const selectedValues = useMemo(() => {
    return options
      .filter((option) => selectedItems.includes(option.value))
      .map((option) => option.label);
  }, [options, selectedItems]);

  const toggleValue = (value: string) => {
    if (selectedItems.includes(value)) {
      onChange(selectedItems.filter((v) => v !== value));
    } else {
      onChange([...selectedItems, value]);
    }
  };

  return (
    <div>
      <label
        className={cn(
          "font-semibold text-body-loud text-md leading-2xl",
          disabled && "!text-on-disabled",
          srOnlyLabel && "sr-only",
        )}
        htmlFor={name}
      >
        {label}{" "}
        {required && (
          <span
            className={cn(
              "text-sm text-danger",
              disabled && "!text-on-disabled",
            )}
          >
            *
          </span>
        )}
      </label>

      {/* Popover CONTROLADO */}
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={buttonRef}
            type="button"
            disabled={disabled}
            variant="tertiary"
            onPointerDown={(e) => {
              e.preventDefault();
              setOpen((v) => !v);
            }}
            onClick={(e) => e.preventDefault()}
            className={cn(
              "flex justify-between items-center group gap-3",
              inputVariants({
                className: cn(
                  className,
                  "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
                  "font-normal text-body-loud",
                ),
                ...props,
              }),
            )}
          >
            <div className="flex items-center gap-3">
              {triggerIcon && triggerIcon}
              {selectedValues.length > 0
                ? `${selectedValues.join(", ")}`
                : placeholder || "Select..."}
            </div>
            <div className="flex items-center justify-center">
              <Icon
                type="far"
                name={isLoading ? "spinner-third" : "angle-down"}
                className={cn(
                  "text-current",
                  isLoading && "animate-spin",
                  "group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body",
                )}
              />
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-full p-2 overflow-hidden shadow-neutral-md"
          style={{ width: buttonRef.current?.offsetWidth || undefined }}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="max-h-60 overflow-y-auto w-full">
            {options.length === 0 && !isLoading && (
              <div className="h-20 flex items-center justify-center">
                <p className="text-body-quiet text-sm text-center">
                  {emptyStateMessage || "No options available"}
                </p>
              </div>
            )}

            {options.length > 0 &&
              options.map(({ label, value }) => {
                const isSelected = selectedItems.includes(value);
                return (
                  <div
                    key={value}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleValue(value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleValue(value);
                      }
                    }}
                    className={cn(
                      "flex justify-between w-full p-2 hover:bg-neutral-object-accent-silent rounded-lg text-body cursor-pointer select-none",
                      isSelected && "bg-surface-action-accent text-action",
                    )}
                  >
                    <p className="font-normal leading-3xl">{label}</p>
                    <Checkbox checked={isSelected} readOnly />
                  </div>
                );
              })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MultiSelect;
